import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import {
  applyAction,
  createRng,
  scrambleEngine,
  toScrambleView,
  type ReadingBand,
  type ScrambleAction,
  type ScrambleState,
  type ScrambleView,
} from '@hazel/shared'

import { api } from '../api/client.js'
import type { DealtRound } from './useRound.js'
import { useMoveLog } from './useMoveLog.js'
import { useMilestone } from './useMilestone.js'

const SOLO_PLAYER = 'solo'

const PLAYER = { id: SOLO_PLAYER, displayName: 'You', userId: null } as const

interface Game {
  readonly seed: string
  readonly state: ScrambleState
  /** Only the moves the engine accepted, so a server replay follows the same path. */
  readonly actions: readonly ScrambleAction[]
  readonly startedAt: number
}

type GameEvent = { type: 'play'; action: ScrambleAction; now: number }

function start(seed: string, band: ReadingBand, now: number): Game {
  return {
    seed,
    state: scrambleEngine.createInitialState({ band, startedAt: now }, [PLAYER], createRng(seed)),
    actions: [],
    startedAt: now,
  }
}

/** Pure, so React re-running it cannot record a game twice. */
function reduceGame(game: Game, event: GameEvent): Game {
  const outcome = applyAction(scrambleEngine, game.state, event.action, {
    playerId: SOLO_PLAYER,
    now: event.now,
  })

  if (!outcome.ok) return game

  return { ...game, state: outcome.state, actions: [...game.actions, event.action] }
}

export interface Scramble {
  readonly view: ScrambleView
  readonly answer: (word: string) => void
  readonly shuffle: () => void
  readonly askForHint: () => void
  readonly again: () => void
  readonly finished: boolean
  /** The milestone this round crossed, if it crossed one. Null the rest of the time. */
  readonly milestone: number | null
}

export function useScramble(
  round: DealtRound<{ band: ReadingBand }>,
  onFinished: () => void,
): Scramble {
  const [game, dispatch] = useReducer(reduceGame, round.seed, (seed) =>
    start(seed, round.config.band, Date.now()),
  )

  const moves = useMoveLog(round.roundId)

  const award = useMilestone()
  const recorded = useRef(false)
  const finished = game.state.status !== 'playing'

  useEffect(() => {
    if (!finished || recorded.current) return
    recorded.current = true

    void moves
      .flush()
      .then((told) => {
        // Nothing to score if the server never heard the whole round. The round
        // is not spent, so it can still be finished from another device — and
        // asking now would only have it refused.
        if (!told) return undefined

        return api('/api/plays', {
          method: 'POST',
          body: JSON.stringify({ roundId: round.roundId }),
        })
      })
      .then(award.notice)
      // A failure to record must not interrupt anything. She solved it either
      // way, and a network blip is not her problem.
      .catch(() => undefined)
  }, [finished, round.roundId, moves, award.notice])

  const play = useCallback(
    (action: ScrambleAction) => {
      const now = Date.now()
      dispatch({ type: 'play', action, now })
      moves.record(action, Math.max(0, now - game.startedAt))
    },
    [moves, game.startedAt],
  )

  return {
    view: useMemo(() => toScrambleView(game.state), [game.state]),
    answer: useCallback((word: string) => play({ type: 'guess', word }), [play]),
    shuffle: useCallback(() => play({ type: 'shuffle' }), [play]),
    askForHint: useCallback(() => play({ type: 'hint' }), [play]),
    again: onFinished,
    finished,
    milestone: award.milestone,
  }
}
